import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Phone, MessageCircle, ArrowRight } from "lucide-react";
import { CONTACT } from "../../contact";
import { SHOW_CONTACT, FACTS_APPROVED } from "../../companyFacts";
import { DEPARTMENTS } from "../../departments";

export function RdMobileBar() {
  return (
    <nav className="rd-mbar" aria-label="Quick contact" data-testid="mobile-cta-bar">
      {SHOW_CONTACT && <a href={"tel:" + CONTACT.phoneRaw} data-testid="mbar-call"><Phone size={15} /> Call</a>}
      {SHOW_CONTACT && <a href={CONTACT.wa} target="_blank" rel="noopener noreferrer" data-testid="mbar-whatsapp"><MessageCircle size={15} /> WhatsApp</a>}
      <Link className="cta" to="/#contact" data-testid="mbar-enquire">Start a Project <ArrowRight size={14} /></Link>
    </nav>
  );
}

const NAV = [
  ["About", "/#about"],
  ["What We Source", "/what-we-source"],
  ["Services", "/#services"],
  ["Global Markets", "/international-sourcing"],
  ["Quality Control", "/#quality"],
  ["Sourcing Stress Test", "/sourcing-stress-test"],
  ["Insights", "/insights"],
  ["Contact", "/#contact"],
];

const MARKETS_MENU = [
  ["USA", "/usa-india-sourcing"],
  ["UK", "/uk-india-sourcing"],
  ["Australia", "/australia-india-sourcing"],
  ["South Africa", "/south-africa-india-sourcing"],
  ["Europe", "/europe-india-sourcing"],
  ["Germany", "/germany-india-sourcing"],
  ["France", "/france-india-sourcing"],
  ["Netherlands", "/netherlands-india-sourcing"],
  ["Sweden", "/sweden-india-sourcing"],
  ["Denmark", "/denmark-india-sourcing"],
  ["Italy", "/italy-india-sourcing"],
  ["Spain", "/spain-india-sourcing"],
  ["Finland", "/finland-india-sourcing"],
  ["Middle East & GCC", "/middle-east-india-sourcing"],
  ["All global markets", "/international-sourcing"],
];

export function RdIntro() {
  const reduce = useReducedMotion();
  const seen = typeof window !== "undefined" && sessionStorage.getItem("rd_intro_seen");
  const [show, setShow] = useState(!seen && !reduce);
  useEffect(() => {
    if (!show) return;
    sessionStorage.setItem("rd_intro_seen", "1");
    const t = setTimeout(() => setShow(false), 1750);
    return () => clearTimeout(t);
  }, [show]);
  return (
    <AnimatePresence>
      {show && (
        <motion.div className="rd-intro" initial={{ opacity: 1 }} exit={{ y: "-100%" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          <motion.div className="ri-wm" initial={{ y: "110%" }} animate={{ y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>Sourcing India</motion.div>
          <motion.div className="ri-sub" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}>Garment, home &amp; houseware sourcing from India</motion.div>
          <motion.div className="ri-line" initial={{ width: 0 }} animate={{ width: "clamp(120px,24vw,260px)" }}
            transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function RdHeader() {
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 48 || loc.pathname !== "/");
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [loc.pathname]);
  useEffect(() => { setOpen(false); }, [loc]);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);
  return (
    <>
      <header className={"rd-hd" + (solid ? " solid" : "")}>
        <div className="rd-hd-in">
          <Link className="rd-logo" to="/" data-testid="rd-logo">
            <b>Sourcing India</b><span>We work for the buyer</span>
          </Link>
          <nav className="rd-nav" aria-label="Primary">
            {NAV.map(([t, h]) => (
              t === "What We Source" ? (
                <div className="rd-navdd" key={t}>
                  <Link to={h} data-testid="nav-what-we-source">{t}</Link>
                  <div className="rd-ddmenu rd-ddmega" data-testid="nav-departments-menu">
                    {DEPARTMENTS.map((g) => (
                      <div key={g.id}><span className="dd-h">{g.name}</span>
                        {g.items.map(([it, ih]) => <Link key={ih} to={ih}>{it}</Link>)}</div>
                    ))}
                    <Link className="dd-all" to="/what-we-source">All categories</Link>
                    <Link className="dd-all" to="/ai-sourcing-assistant" data-testid="nav-ai-assistant">AI Sourcing Assistant</Link>
                  </div>
                </div>
              ) : t === "Global Markets" ? (
                <div className="rd-navdd" key={t}>
                  <Link to={h} data-testid="nav-global-markets">{t}</Link>
                  <div className="rd-ddmenu" data-testid="nav-markets-menu">
                    {MARKETS_MENU.map(([mt, mh]) => <Link key={mt} to={mh}>{mt}</Link>)}
                  </div>
                </div>
              ) : <Link key={t} to={h}>{t}</Link>
            ))}
          </nav>
          <Link className="rd-enq desk" to="/#contact" data-testid="nav-start-project-button">Enquire</Link>
          <button className={"rd-burger" + (open ? " x" : "")} aria-label="Menu" aria-expanded={open}
            onClick={() => setOpen((o) => !o)} data-testid="rd-burger"><span></span><span></span><span></span></button>
        </div>
      </header>
      <div className={"rd-mnav" + (open ? " open" : "")}>
        {NAV.map(([t, h]) => <Link key={t} to={h} onClick={() => setOpen(false)}>{t}</Link>)}
        <Link className="enq" to="/#contact" onClick={() => setOpen(false)}>Start a Sourcing Project →</Link>
      </div>
    </>
  );
}

export function RdFooter() {
  const y = new Date().getFullYear();
  return (
    <footer className="rd-ft">
      <div className="wrapx">
        <p className="fbig">Buyer-side sourcing &amp; quality control, based in India.</p>
        <p className="note">Sourcing India works for the buyer — not the factory. We are not a factory, a marketplace or a customs broker.</p>
        <div className="cols">
          <div><h5>Company</h5><ul>
            <li><Link to="/#about">About</Link></li>
            {FACTS_APPROVED.founderTitle && <li><Link to="/#founder">Founder</Link></li>}
            <li><Link to="/case-studies">Sourcing Guides</Link></li>
            <li><Link to="/sourcing-stress-test">Sourcing Stress Test</Link></li>
            <li><Link to="/#quality">Quality Control</Link></li>
            <li><Link to="/#contact">Contact</Link></li>
          </ul></div>
          <div><h5>What we source</h5><ul>
            <li><Link to="/what-we-source">All Categories</Link></li>
            <li><Link to="/ai-sourcing-assistant" data-testid="footer-ai-assistant">AI Sourcing Assistant</Link></li>
            {DEPARTMENTS.map((g) => <li key={g.id}><Link to={"/what-we-source#" + g.id}>{g.name}</Link></li>)}
            <li><Link to="/pet-products-sourcing-india">Pet Products</Link></li>
          </ul></div>
          <div><h5>Services</h5><ul>
            <li><Link to="/product-development-india">Product Development</Link></li>
            <li><Link to="/factory-sourcing-india">Factory Sourcing</Link></li>
            <li><Link to="/quality-control-india">Quality Assurance</Link></li>
            <li><Link to="/factory-audit-india">Factory Inspection</Link></li>
            <li><Link to="/private-label-manufacturing-india">Private Label Sourcing</Link></li>
          </ul></div>
          <div><h5>Global markets</h5><ul>
            <li><Link to="/usa-india-sourcing">USA</Link></li>
            <li><Link to="/uk-india-sourcing">UK</Link></li>
            <li><Link to="/europe-india-sourcing">Europe</Link></li>
            <li><Link to="/australia-india-sourcing">Australia</Link></li>
            <li><Link to="/south-africa-india-sourcing">South Africa</Link></li>
            <li><Link to="/middle-east-india-sourcing">Middle East &amp; GCC</Link></li>
            <li><Link to="/international-sourcing">All markets</Link></li>
          </ul></div>
          <div><h5>Get in touch</h5><ul>
            {SHOW_CONTACT && <li><a href={"mailto:" + CONTACT.email}>{CONTACT.email}</a></li>}
            {SHOW_CONTACT && <li><a href={"tel:" + CONTACT.phoneRaw}>{CONTACT.phone}</a></li>}
            {SHOW_CONTACT && <li><a href={CONTACT.wa} target="_blank" rel="noopener noreferrer">WhatsApp</a></li>}
            {FACTS_APPROVED.address && <li>D-8, Manarover Colony, Delhi Road, Moradabad, UP 244001</li>}
            <li><Link to="/#contact" data-testid="footer-enquiry-link">Send an enquiry</Link></li>
          </ul></div>
        </div>
        <div className="bot">
          <span>© {y} Sourcing India · sourcingindia.org</span>
          <span>Buyer-side sourcing &amp; quality control{FACTS_APPROVED.address ? " · Moradabad, India" : " · India"}</span>
        </div>
      </div>
    </footer>
  );
}
